import React from "react";

interface QuantityControlProps {
  value: number;
  onChange: (newValue: number) => void;
  min?: number;
  max?: number;
  disabled?: boolean;
  ariaLabel: string;
  className?: string;
}

const QuantityControl: React.FC<QuantityControlProps> = ({
  value,
  onChange,
  min = 1,
  max = 99,
  disabled = false,
  ariaLabel,
  className = "",
}) => {
  // Enforce the business rule: the effective maximum is the smaller of the passed max prop and 20.
  const effectiveMax = Math.min(max, 20);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = parseInt(e.target.value, 10);
    // Allow typing, but reset to min if input is cleared or invalid
    onChange(isNaN(newValue) || newValue < min ? min : newValue);
  };

  const handleBlur = () => {
    // On blur, clamp the value to the allowed range using the effective max
    const clampedValue = Math.max(min, Math.min(value, effectiveMax));
    if (clampedValue !== value) {
      onChange(clampedValue);
    }
  };

  const handleIncrement = () => {
    if (value < effectiveMax) {
      onChange(value + 1);
    }
  };

  const handleDecrement = () => {
    if (value > min) {
      onChange(value - 1);
    }
  };

  return (
    <div className={`quantity-control ${className}`}>
      <button
        onClick={handleDecrement}
        disabled={disabled || value <= min}
        className="quantity-control__button"
        aria-label="Decrease quantity"
      >
        -
      </button>
      <input
        type="number"
        value={value}
        onChange={handleInputChange}
        onBlur={handleBlur}
        min={min}
        max={effectiveMax} // Use the effective max here
        className="quantity-control__input"
        aria-label={ariaLabel}
        disabled={disabled}
      />
      <button
        onClick={handleIncrement}
        disabled={disabled || value >= effectiveMax} // Use the effective max here
        className="quantity-control__button"
        aria-label="Increase quantity"
      >
        +
      </button>
    </div>
  );
};

export default QuantityControl;
